import { useState } from "react";

//HOOKS
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { setTermsAccepted } from "../../store/slices/termsAndConditionsSlice";
import { logout } from "../../store/slices/authSlice";

//UI COMPONENTS
import Checkbox from "../Checkbox/Checkbox";
import CustomModal from "../CustomModal/CustomModal";
import Button from "../Button/Button";
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import ToastMessage from "../ToastMessage/ToastMessage";

//ASSETS
import TermsIcon from "../../assets/images/illustrations/terms-and-conditions.svg?react";

//UTILS
import * as Sentry from "@sentry/react";
import { addMessage } from "../../utils/addMessage";
import { genericErrorMessage } from "../../utils";
import { signTermsAndConditions } from "../../connectors/operationApi";
import { sentryHttpExtras } from "../../connectors/sentryAxiosInterceptor";
import { openExternal } from "../NavBar/utils/utils";

//STYLES
import styles from "./TermsAndConditionsModal.module.scss";

const BASE_URL = import.meta.env.VITE_BASE_URL;
const TERMS_AND_CONDITIONS_URL = `${BASE_URL}/doc/Términos-y-Condiciones.pdf`;
const PRIVACY_URL = `${BASE_URL}/doc/Politicas-de-Privacidad-Allaria.pdf`;

const TermsAndConditionsModal = () => {
  const {
    container,
    imageWrapper,
    defaultIcon,
    buttonsContainer,
    checkboxContainer,
    link,
    toastContainer,
    toast,
  } = styles;

  const dispatch = useAppDispatch();

  const { hasAccepted } = useAppSelector((state) => state.termsAndConditions);
  const token = useAppSelector(
    (state) => state.authentication.auth?.access_token,
  );
  const { accounts } = useAppSelector((state) => state.userAccounts);

  const [checked, setChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const accountId = accounts[0]?.number;
  // Don't open the modal until accounts have loaded: signing T&C requires an
  // accountId, and accounts[0] is still undefined while accounts are loading.
  const isOpen = hasAccepted === false && !!accountId;

  const handleAccept = async () => {
    if (!checked || !token || !accountId) return;

    setAccepting(true);

    try {
      await signTermsAndConditions({ accountId });

      dispatch(setTermsAccepted());
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          accountId,
          hasToken: !!token,
          ...sentryHttpExtras(error),
        },
        tags: { module: "TermsAndConditionsModal", action: "handleAccept" },
      });
      addMessage({
        messageId: "tycToast",
        message: genericErrorMessage,
        type: "error",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={() => {}}
      hideCloseButton
      isDismissable={false}
    >
      <ModalContent>
        <ModalHeader />
        <ModalBody className={container}>
          <ToastMessage
            messageId="tycToast"
            position="top-center"
            className={toastContainer}
            toastClassName={toast}
          />

          <div className={imageWrapper}>
            <TermsIcon className={defaultIcon} />
          </div>

          <h1>Aceptación de Términos y Condiciones</h1>

          <div className={checkboxContainer}>
            <Checkbox
              value={checked ? "YES" : "NO"}
              isSelected={checked}
              onChange={() => setChecked((prev) => !prev)}
            />
            <p onClick={() => setChecked((prev) => !prev)}>
              He leído y acepto los{" "}
              <a
                href={TERMS_AND_CONDITIONS_URL}
                target="_blank"
                rel="noreferrer"
                className={link}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openExternal(TERMS_AND_CONDITIONS_URL, "_blank");
                }}
              >
                Términos y Condiciones
              </a>{" "}
              y la{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noreferrer"
                className={link}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openExternal(PRIVACY_URL, "_blank");
                }}
              >
                Políticas de Privacidad
              </a>
              .
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className={buttonsContainer}>
            <Button
              text="Cerrar sesión"
              onPress={handleLogout}
              variant="secondary"
              isDisabled={accepting}
            />
            <Button
              text="Continuar"
              onPress={handleAccept}
              isDisabled={!checked}
              isLoading={accepting}
            />
          </div>
        </ModalFooter>
      </ModalContent>
    </CustomModal>
  );
};

export default TermsAndConditionsModal;
